import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  CheckCircle2, Clock, Sparkles, Camera, ArrowRight, X
} from 'lucide-react';
import { markSessionPosted, clearCurrentSession, hasPendingPostSession, trackPostEvent, POST_EVENTS } from '../utils/postAnalytics';
import { toast } from 'sonner';

/**
 * PostConfirmationPrompt - "Did you post?" confirmation loop
 * 
 * Shows after talent uses Post Activation tools
 * Tracks posting behavior for analytics
 */

const PostConfirmationPrompt = ({ 
  isOpen, 
  onClose,
  venueName = 'your activation'
}) => {
  const [confirmed, setConfirmed] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);
  
  const handleYes = () => {
    setConfirmed(true);
    markSessionPosted(true);
    setShowThankYou(true);
    
    // Show success after delay
    setTimeout(() => {
      toast.success('Awesome! Keep posting to increase your visibility 🚀');
      clearCurrentSession();
      onClose();
    }, 1500);
  };
  
  const handleNotYet = () => {
    setConfirmed(false);
    markSessionPosted(false);
    
    toast.info('No worries! You can post anytime from your dashboard.', {
      action: {
        label: 'Remind Me Later',
        onClick: () => {
          // Could set a reminder notification here
          toast.success('We\'ll remind you in 1 hour');
        }
      }
    });
    
    clearCurrentSession();
    onClose();
  };
  
  const handleSkip = () => {
    trackPostEvent(POST_EVENTS.POST_CONFIRMED_NO, { skipped: true });
    clearCurrentSession();
    onClose();
  };
  
  if (showThankYou) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-sm bg-gradient-to-br from-green-50 to-white border-green-200">
          <DialogHeader>
            <DialogTitle className="sr-only">You're building visibility</DialogTitle>
            <DialogDescription className="sr-only">Post confirmed — thank-you screen</DialogDescription>
          </DialogHeader>
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">You're building visibility!</h3>
            <p className="text-sm text-gray-600 mt-2">
              Consistent posters get 3x more bookings
            </p>
            <Badge className="mt-4 bg-green-100 text-green-700 border-green-200">
              <Sparkles className="w-3 h-3 mr-1" />
              +1 Post This Week
            </Badge>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm bg-gradient-to-br from-pink-50 to-white border-pink-200">
        <DialogHeader>
          <DialogTitle className="text-center flex flex-col items-center gap-2">
            <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center">
              <Camera className="w-6 h-6 text-pink-600" />
            </div>
            <span className="text-lg">Did you post?</span>
          </DialogTitle>
          <DialogDescription className="sr-only">Did you post?</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <p className="text-center text-sm text-gray-600">
            Let us know if you shared your content from {venueName}
          </p>
          
          <div className="flex gap-3">
            <Button
              className="flex-1 bg-green-500 hover:bg-green-600 text-white"
              onClick={handleYes}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Yes, I posted!
            </Button>
            
            <Button
              variant="outline"
              className="flex-1 border-gray-300"
              onClick={handleNotYet}
            >
              <Clock className="w-4 h-4 mr-2" />
              Not yet
            </Button>
          </div>
          
          <button
            onClick={handleSkip}
            className="w-full text-xs text-gray-400 hover:text-gray-600 py-2"
          >
            Skip this
          </button>
        </div>
        
        {/* Motivation tip */}
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-center">
          <p className="text-xs text-amber-700">
            💡 Talent who post within 1 hour get <strong>40% more engagement</strong>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PostConfirmationPrompt;
